import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Stack, Button, TextField, Chip, Alert, InputAdornment, Snackbar, CircularProgress, Grid, MenuItem, Checkbox, ListItemText, InputBase,
} from '@mui/material';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import { useSelector } from 'react-redux';
import { selectActiveEntityId, ALL_ENTITY_ID } from '../../redux/slices/orgSlice';
import { solidBtn, tonalBtn, field, Panel, SkeletonRows, LoadAlert } from '../uiKit';
import http, { apiErrorMessage } from '../../Api/http';
import { GetCareersSettings, SaveCareersSettings, GetEmployees } from '../../Api/Api';

const NAME_RE = /^[a-z0-9-]{3,60}$/;

// Mirrors the backend: only the origin is kept, so "/careers" is never doubled.
export const originOf = (url) => {
    try { return new URL(url.trim()).origin; } catch { return ''; }
};

export default function CareersSettingsTab() {
    const activeEntityId = useSelector(selectActiveEntityId);

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState(null);
    const [tried, setTried] = useState(false);

    const [suggested, setSuggested] = useState('');
    const [isSuggested, setIsSuggested] = useState(false);
    const [name, setName] = useState('');
    const [siteBaseUrl, setSiteBaseUrl] = useState('');
    const [initialSite, setInitialSite] = useState('');
    const [coolingDays, setCoolingDays] = useState('90');
    const [recipients, setRecipients] = useState([]);
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [userSearch, setUserSearch] = useState('');

    const apply = useCallback((d) => {
        setName(d?.careersName || '');
        setSuggested(d?.isSuggested ? (d?.careersName || '') : '');
        setIsSuggested(Boolean(d?.isSuggested));
        setSiteBaseUrl(d?.siteBaseUrl || '');
        setInitialSite(d?.siteBaseUrl || '');
        setCoolingDays(String(d?.coolingDays ?? 90));
        setRecipients(Array.isArray(d?.alertRecipients) ? d.alertRecipients : []);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        if (activeEntityId === ALL_ENTITY_ID) {
            setLoadError('Pick a single business entity in the sidebar to manage its careers settings.');
            setLoading(false);
            return;
        }
        try {
            const { data: body } = await http.get(GetCareersSettings);
            if (body?.error) throw new Error(body.message || 'Could not load careers settings.');
            apply(body.data);
            setLoadError('');
        } catch (err) {
            const status = err?.response?.status;
            setLoadError(apiErrorMessage(err, 'Could not load careers settings.') + (status ? ` (HTTP ${status})` : ''));
        } finally {
            setLoading(false);
        }
    }, [apply, activeEntityId]);

    useEffect(() => { load(); }, [load, activeEntityId]);

    // Alert recipients are stored as login IDs; an employee's login ID is their employee code.
    useEffect(() => {
        let alive = true;
        (async () => {
            setUsersLoading(true);
            try {
                const { data: body } = await http.get(GetEmployees, { params: { search: '', page: 1, pageSize: 1000 } });
                const items = body?.data?.items || [];
                if (alive) {
                    setUsers(items
                        .filter((r) => r.employeeCode && String(r.status || 'active').toLowerCase() === 'active')
                        .map((r) => ({ loginId: r.employeeCode, name: String(r.fullName || r.name || r.employeeCode).trim() })));
                }
            } catch { if (alive) setUsers([]); } finally { if (alive) setUsersLoading(false); }
        })();
        return () => { alive = false; };
    }, [activeEntityId]);

    const userOptions = useMemo(() => {
        const known = new Set(users.map((u) => u.loginId));
        return [...users, ...recipients.filter((id) => !known.has(id)).map((id) => ({ loginId: id, name: id }))];
    }, [users, recipients]);

    const shownUsers = useMemo(() => {
        const q = userSearch.trim().toLowerCase();
        return q ? userOptions.filter((u) => `${u.name} ${u.loginId}`.toLowerCase().includes(q)) : userOptions;
    }, [userOptions, userSearch]);

    const nameBad = name.trim() !== '' && !NAME_RE.test(name.trim().toLowerCase());
    const siteTrim = siteBaseUrl.trim();
    const siteBad = siteTrim !== '' && !(/^https:\/\//i.test(siteTrim) && originOf(siteTrim));
    const daysNum = Number(coolingDays);
    const daysBad = coolingDays === '' || !Number.isInteger(daysNum) || daysNum < 0 || daysNum > 365;

    const domain = siteTrim && !siteBad ? originOf(siteTrim) : '';
    const previewName = name.trim().toLowerCase() || suggested || 'your-company';
    const preview = `${domain}/careers/${previewName}`;

    const save = async () => {
        setTried(true);
        if (nameBad || siteBad || daysBad) return;
        const payload = {
            careersName: name.trim().toLowerCase(),
            coolingDays: daysNum,
            alertRecipients: recipients,
        };
        // Leaving the key out keeps the stored value, so only send it when it changed.
        if (siteTrim !== initialSite) payload.siteBaseUrl = siteTrim;

        setSaving(true);
        try {
            const { data: body } = await http.put(SaveCareersSettings, payload);
            if (body?.error) throw new Error(body.message || 'Could not save careers settings.');
            apply(body.data);
            setTried(false);
            setSnack({ msg: body.message || 'Careers settings saved.', sev: 'success' });
        } catch (err) {
            setSnack({ msg: apiErrorMessage(err, 'Could not save careers settings.'), sev: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 2, pt: 1.5 }}>
            <Panel title="Careers settings" icon={PublicRoundedIcon} hint="Controls the public careers page and its links">
                {loading ? (
                    <SkeletonRows count={4} height={58} />
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }}
                            action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                            {loadError}
                        </LoadAlert>
                    </Box>
                ) : (
                    <Box sx={{ p: 2.2 }}>
                        {!initialSite && (
                            <Alert severity="warning" sx={{ mb: 2, borderRadius: '9px' }}>
                                Set your website address in Careers settings before publishing. Vacancies can still be saved.
                            </Alert>
                        )}
                        {isSuggested && (
                            <Alert severity="info" sx={{ mb: 2, borderRadius: '9px' }}>
                                This name will be saved the first time you publish a vacancy.
                            </Alert>
                        )}

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Careers page name" size="small" fullWidth value={name}
                                    onChange={(e) => setName(e.target.value.toLowerCase())}
                                    error={tried && nameBad}
                                    helperText={nameBad ? 'Lowercase letters, digits and hyphens only (3 to 60 characters).' : 'Leave blank to use the suggested name.'}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: {
                                            endAdornment: isSuggested ? (
                                                <InputAdornment position="end">
                                                    <Chip label="Suggested" size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 800 }} />
                                                </InputAdornment>
                                            ) : null,
                                        },
                                    }}
                                    sx={field}
                                />
                                {suggested && name !== suggested && (
                                    <Button size="small" startIcon={<AutoFixHighRoundedIcon sx={{ fontSize: 15 }} />}
                                        onClick={() => setName(suggested)} sx={{ ...tonalBtn, mt: -1.2, height: 28, fontSize: 11.5 }}>
                                        Use suggested
                                    </Button>
                                )}
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Website address" size="small" fullWidth value={siteBaseUrl}
                                    onChange={(e) => setSiteBaseUrl(e.target.value)}
                                    placeholder="https://yourcompany.com"
                                    error={tried && siteBad}
                                    helperText={siteBad
                                        ? 'Enter a full https:// address, for example https://discovermarketing.co'
                                        : "Enter the website's domain only, for example https://discovermarketing.co. The /careers part is added automatically."}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={field}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    label="Cooling period (days)" type="number" size="small" fullWidth value={coolingDays}
                                    onChange={(e) => setCoolingDays(e.target.value)}
                                    error={tried && daysBad}
                                    helperText={daysBad ? 'Enter a whole number from 0 to 365.' : 'How long a rejected candidate must wait before applying again. 0 turns this off.'}
                                    slotProps={{ htmlInput: { min: 0, max: 365 }, inputLabel: { shrink: true } }}
                                    sx={field}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    select size="small" fullWidth label="Alert recipients"
                                    helperText="These people get an inbox alert for every application from the careers page."
                                    value={recipients}
                                    onChange={(e) => setRecipients(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        select: {
                                            multiple: true,
                                            displayEmpty: true,
                                            onClose: () => setUserSearch(''),
                                            MenuProps: { slotProps: { paper: { sx: { maxHeight: 340, width: 360, borderRadius: '9px' } } } },
                                            renderValue: (sel) => (sel.length === 0 ? (
                                                <Typography sx={{ fontSize: 14, color: '#98A0AE' }}>{usersLoading ? 'Loading people…' : 'Pick people'}</Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {sel.map((id) => {
                                                        const u = userOptions.find((x) => x.loginId === id);
                                                        return (
                                                            <Chip key={id} size="small" label={u ? u.name : id}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                onDelete={() => setRecipients((r) => r.filter((x) => x !== id))}
                                                                sx={{ fontWeight: 700, fontSize: 12 }} />
                                                        );
                                                    })}
                                                </Box>
                                            )),
                                        },
                                    }}
                                    sx={field}
                                >
                                    <Box sx={{ px: 1.5, py: 1, position: 'sticky', top: 0, bgcolor: '#fff', zIndex: 1 }} onKeyDown={(e) => e.stopPropagation()}>
                                        <InputBase fullWidth autoFocus placeholder="Search people…" value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                            sx={{ fontSize: 13, px: 1.2, height: 34, border: '1px solid #E6EAF1', borderRadius: '7px' }} />
                                    </Box>
                                    {shownUsers.length === 0 && (
                                        <MenuItem disabled sx={{ fontSize: 13 }}>{usersLoading ? 'Loading…' : 'No matching people'}</MenuItem>
                                    )}
                                    {shownUsers.map((u) => (
                                        <MenuItem key={u.loginId} value={u.loginId} sx={{ fontSize: 13.5, py: 0.3 }}>
                                            <Checkbox size="small" checked={recipients.includes(u.loginId)} sx={{ p: 0.6, mr: 1 }} />
                                            <ListItemText primary={u.name} secondary={u.name === u.loginId ? null : u.loginId}
                                                slotProps={{ primary: { sx: { fontSize: 13.5, fontWeight: 600 } }, secondary: { sx: { fontSize: 11 } } }} />
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={1} sx={{ mt: 2.4, p: 1.5, alignItems: 'center', borderRadius: '9px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F6' }}>
                            <LinkRoundedIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                            <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>Careers home:</Typography>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155', wordBreak: 'break-all' }}>{preview}</Typography>
                        </Stack>

                        <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 2.4 }}>
                            <Button onClick={save} disabled={saving}
                                startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                                sx={{ ...solidBtn, height: 40, px: 2.6 }}>
                                {saving ? 'Saving…' : 'Save settings'}
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Panel>

            <Snackbar open={Boolean(snack)} autoHideDuration={3600} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
